import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Me } from './auth.models';

/** Opzioni o credenziale WebAuthn in JSON, con i dati binari in base64url. */
export type PasskeyJson = Record<string, unknown>;

/** Sfida WebAuthn emessa dal server: l'identificativo e le opzioni per il browser. */
export interface PasskeyChallenge {
  readonly challengeId: string;
  readonly publicKey: PasskeyJson;
}

/** Opzioni della richiesta al browser: suggerimento automatico e interruzione. */
export interface PasskeyRequestOptions {
  readonly mediation?: CredentialMediationRequirement;
  readonly signal?: AbortSignal;
}

/**
 * L'utente ha chiuso la finestra del dispositivo, non ha confermato in tempo oppure la richiesta
 * è stata interrotta: non è un errore da mostrare come tale.
 */
export class PasskeyCancelledError extends Error {
  constructor() {
    super('Operazione con la passkey annullata.');
    this.name = 'PasskeyCancelledError';
  }
}

/** Il browser non sa usare le passkey. */
export class PasskeyUnsupportedError extends Error {
  constructor() {
    super('Il browser non supporta le passkey.');
    this.name = 'PasskeyUnsupportedError';
  }
}

/** Il costruttore `PublicKeyCredential` con i metodi statici, presenti solo nei browser recenti. */
interface PublicKeyCredentialStatics {
  isUserVerifyingPlatformAuthenticatorAvailable?: () => Promise<boolean>;
  isConditionalMediationAvailable?: () => Promise<boolean>;
  parseCreationOptionsFromJSON?: (options: unknown) => PublicKeyCredentialCreationOptions;
  parseRequestOptionsFromJSON?: (options: unknown) => PublicKeyCredentialRequestOptions;
}

/** Da base64url (con o senza `=`) a byte. */
export function base64UrlToBuffer(value: string): ArrayBuffer {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes.buffer;
}

/** Da byte a base64url senza `=`. */
export function bufferToBase64Url(buffer: ArrayBuffer | ArrayBufferView): string {
  const bytes =
    buffer instanceof ArrayBuffer
      ? new Uint8Array(buffer)
      : new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function toBuffer(value: unknown): ArrayBuffer {
  return typeof value === 'string' ? base64UrlToBuffer(value) : (value as ArrayBuffer);
}

function withBinaryIds(descriptors: unknown): PublicKeyCredentialDescriptor[] | undefined {
  if (!Array.isArray(descriptors)) {
    return undefined;
  }
  return descriptors.map((descriptor: Record<string, unknown>): PublicKeyCredentialDescriptor => ({
    ...descriptor,
    type: 'public-key',
    id: toBuffer(descriptor['id']),
  }));
}

/** Opzioni di registrazione dal JSON del server, convertite a mano (browser senza il metodo nativo). */
export function creationOptionsFromJson(json: PasskeyJson): PublicKeyCredentialCreationOptions {
  const user = (json['user'] ?? {}) as Record<string, unknown>;
  const options: Record<string, unknown> = {
    ...json,
    challenge: toBuffer(json['challenge']),
    user: { ...user, id: toBuffer(user['id']) },
  };
  const exclude = withBinaryIds(json['excludeCredentials']);
  if (exclude) options['excludeCredentials'] = exclude;
  else delete options['excludeCredentials'];
  return options as unknown as PublicKeyCredentialCreationOptions;
}

/** Opzioni di accesso dal JSON del server, convertite a mano. */
export function requestOptionsFromJson(json: PasskeyJson): PublicKeyCredentialRequestOptions {
  const options: Record<string, unknown> = { ...json, challenge: toBuffer(json['challenge']) };
  const allow = withBinaryIds(json['allowCredentials']);
  if (allow) options['allowCredentials'] = allow;
  else delete options['allowCredentials'];
  return options as unknown as PublicKeyCredentialRequestOptions;
}

/** La credenziale come JSON per il server: `toJSON()` del browser o la conversione a mano. */
export function credentialToJson(credential: PublicKeyCredential): PasskeyJson {
  const native = (credential as { toJSON?: () => unknown }).toJSON;
  if (typeof native === 'function') {
    try {
      return native.call(credential) as PasskeyJson;
    } catch {
      // Alcune estensioni dei gestori di password restituiscono oggetti senza `toJSON` valido.
    }
  }
  const response = credential.response as AuthenticatorResponse & Record<string, unknown>;
  const json: Record<string, unknown> = {
    clientDataJSON: bufferToBase64Url(response.clientDataJSON),
  };
  if ('attestationObject' in response) {
    json['attestationObject'] = bufferToBase64Url(response['attestationObject'] as ArrayBuffer);
    const transports = (response as { getTransports?: () => string[] }).getTransports;
    if (typeof transports === 'function') {
      json['transports'] = transports.call(response);
    }
  } else {
    json['authenticatorData'] = bufferToBase64Url(response['authenticatorData'] as ArrayBuffer);
    json['signature'] = bufferToBase64Url(response['signature'] as ArrayBuffer);
    const handle = response['userHandle'] as ArrayBuffer | null | undefined;
    json['userHandle'] = handle ? bufferToBase64Url(handle) : null;
  }
  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment ?? null,
    response: json,
    clientExtensionResults: credential.getClientExtensionResults?.() ?? {},
  };
}

/** Errori del browser che vogliono dire "annullato" (finestra chiusa, tempo scaduto, interruzione). */
function isCancellation(error: unknown): boolean {
  const name = (error as { name?: unknown } | null)?.name;
  return name === 'NotAllowedError' || name === 'AbortError';
}

/**
 * Cerimonie WebAuthn delle passkey (research R-05, R-22): rilevazione del supporto, conversione
 * delle opzioni del server (`parseCreationOptionsFromJSON`/`parseRequestOptionsFromJSON` con un
 * convertitore base64url di riserva), accesso con passkey anche dal suggerimento automatico del
 * campo email (`mediation: 'conditional'`) e conferma dell'identità.
 */
@Injectable({ providedIn: 'root' })
export class PasskeyService {
  private readonly document = inject(DOCUMENT);
  private readonly http = inject(HttpClient);

  private get statics(): PublicKeyCredentialStatics | null {
    const view = this.document.defaultView as
      (Window & { PublicKeyCredential?: PublicKeyCredentialStatics }) | null;
    return view?.PublicKeyCredential ?? null;
  }

  private get container(): CredentialsContainer | null {
    return this.document.defaultView?.navigator?.credentials ?? null;
  }

  /** Il browser offre WebAuthn: si possono usare passkey, anche di un altro dispositivo. */
  isSupported(): boolean {
    const container = this.container;
    return (
      !!this.statics &&
      !!container &&
      typeof container.create === 'function' &&
      typeof container.get === 'function'
    );
  }

  /** Il dispositivo ha un autenticatore integrato (impronta, volto, PIN) per creare passkey. */
  async platformAuthenticatorAvailable(): Promise<boolean> {
    const check = this.statics?.isUserVerifyingPlatformAuthenticatorAvailable;
    if (!this.isSupported() || typeof check !== 'function') {
      return false;
    }
    try {
      return await check.call(this.statics);
    } catch {
      return false;
    }
  }

  /** Il browser propone le passkey nel suggerimento automatico dei campi. */
  async conditionalMediationAvailable(): Promise<boolean> {
    const check = this.statics?.isConditionalMediationAvailable;
    if (!this.isSupported() || typeof check !== 'function') {
      return false;
    }
    try {
      return await check.call(this.statics);
    } catch {
      return false;
    }
  }

  /** Crea una passkey con le opzioni di registrazione del server; restituisce la credenziale in JSON. */
  async create(publicKey: PasskeyJson): Promise<PasskeyJson> {
    const container = this.container;
    if (!this.isSupported() || !container) {
      throw new PasskeyUnsupportedError();
    }
    const options = this.parse(publicKey, 'creation') as PublicKeyCredentialCreationOptions;
    let credential: Credential | null;
    try {
      credential = await container.create({ publicKey: options });
    } catch (error: unknown) {
      if (isCancellation(error)) throw new PasskeyCancelledError();
      throw error;
    }
    if (!credential) {
      throw new PasskeyCancelledError();
    }
    return credentialToJson(credential as PublicKeyCredential);
  }

  /** Chiede al browser una passkey per le opzioni di accesso del server. */
  async get(publicKey: PasskeyJson, request: PasskeyRequestOptions = {}): Promise<PasskeyJson> {
    const container = this.container;
    if (!this.isSupported() || !container) {
      throw new PasskeyUnsupportedError();
    }
    const options = this.parse(publicKey, 'request') as PublicKeyCredentialRequestOptions;
    let credential: Credential | null;
    try {
      credential = await container.get({
        publicKey: options,
        mediation: request.mediation,
        signal: request.signal,
      });
    } catch (error: unknown) {
      if (isCancellation(error)) throw new PasskeyCancelledError();
      throw error;
    }
    if (!credential) {
      throw new PasskeyCancelledError();
    }
    return credentialToJson(credential as PublicKeyCredential);
  }

  /**
   * Accesso con passkey (FR-052, FR-053): opzioni dal server, scelta della passkey, verifica.
   * Con `mediation: 'conditional'` la scelta avviene dal suggerimento automatico del campo email.
   */
  async signIn(rememberMe: boolean, request: PasskeyRequestOptions = {}): Promise<Me> {
    const challenge = await firstValueFrom(
      this.http.post<PasskeyChallenge>('/api/auth/passkeys/login/options', null),
    );
    const credential = await this.get(challenge.publicKey, request);
    return firstValueFrom(
      this.http.post<Me>('/api/auth/passkeys/login', {
        challengeId: challenge.challengeId,
        credential,
        rememberMe,
      }),
    );
  }

  /** Conferma dell'identità con una passkey (research R-10). */
  async reauthenticate(): Promise<void> {
    const challenge = await firstValueFrom(
      this.http.post<PasskeyChallenge>('/api/auth/passkeys/reauth/options', null),
    );
    const credential = await this.get(challenge.publicKey);
    await firstValueFrom(
      this.http.post('/api/auth/reauthenticate/passkey', {
        challengeId: challenge.challengeId,
        credential,
      }),
      { defaultValue: null },
    );
  }

  private parse(
    json: PasskeyJson,
    kind: 'creation' | 'request',
  ): PublicKeyCredentialCreationOptions | PublicKeyCredentialRequestOptions {
    const statics = this.statics;
    try {
      if (kind === 'creation' && typeof statics?.parseCreationOptionsFromJSON === 'function') {
        return statics.parseCreationOptionsFromJSON(json);
      }
      if (kind === 'request' && typeof statics?.parseRequestOptionsFromJSON === 'function') {
        return statics.parseRequestOptionsFromJSON(json);
      }
    } catch {
      // Opzioni con campi che il metodo nativo non accetta: si usa il convertitore di riserva.
    }
    return kind === 'creation' ? creationOptionsFromJson(json) : requestOptionsFromJson(json);
  }
}

/** `true` se l'errore vuol dire che l'utente ha annullato l'uso della passkey. */
export function isPasskeyCancelled(error: unknown): boolean {
  return error instanceof PasskeyCancelledError;
}
